//#region Imports Packages and Libs

import { store } from "../store/store";
import { UserService } from "../service/user_service";
import { StorageHelper } from "./storage_helper";
import { Util } from "../util";

//#endregion

//#region Admin Mode Persistence Helpers

/**
 * SD-13787: Restoring admin mode after a reload is a server round-trip, but the
 * welcome message request and the first outbound message are triggered by SignalR
 * connecting - a completely separate signal. Nothing ordered the two, so both went
 * out with `adminMode: 0` while the user was still logged in.
 *
 * The restore now runs once from InitConfig and everything that carries the flag
 * awaits it through `ensureAdminModeRestored()` before building its payload.
 */
let restorePromise: Promise<void> | null = null;

/**
 * Set once the restore has either finished or been waited out. After that nobody waits again:
 * a hung `checkUserStatus` leaves `restorePromise` pending forever, so without this latch every
 * later message would race the same dead promise and pay the timeout again - turning one slow
 * turn into a permanent delay on every message for the rest of the session.
 */
let waitAbandoned = false;

/**
 * How long an outbound message will wait for the admin mode restore before giving up.
 * `sendAdminGet` uses a bare `fetch` with no timeout, so without this cap a stalled
 * `checkUserStatus` would leave the welcome message and every user message awaiting a
 * promise that never settles - a dead chat window, which is far worse than one turn
 * reported with `adminMode: 0`.
 */
const RESTORE_WAIT_TIMEOUT_MS = 3000;

/**
 * Builds the local storage key holding the persisted admin mode flag.
 * @returns the storage key for the current page and external user.
 */
export const getAdminModeStorageKey = function (): string {
    const pageId = store.state.pageId || "";
    const extUserId = new UserService().getExtUserId();
    return `swc_is_adm_${pageId}_${extUserId}`;
};

/**
 * Reads the persisted admin mode flag written at login time.
 * @returns true when the last known state for this page/user was "logged in".
 */
export const isAdminModePersisted = function (): boolean {
    try {
        const storedValue = StorageHelper.get(getAdminModeStorageKey()) || "";
        if (Util.isNullOrEmpty(storedValue)) return false;
        return window.atob(storedValue) === "1";
    } catch {
        // A malformed or non-base64 value means we have nothing trustworthy to restore.
        return false;
    }
};

/**
 * Persists the admin mode flag so it survives a page reload.
 * @param isAdminMode - the state to persist.
 */
export const setAdminModePersisted = function (isAdminMode: boolean) {
    try {
        StorageHelper.set(getAdminModeStorageKey(), window.btoa(isAdminMode ? "1" : "0"));
    } catch (ex) {
        console.log("[admin_mode.ts] An error has occurred \n-> Inside setAdminModePersisted -> Error: ", ex);
    }
};

/**
 * Asks the server whether the persisted admin session is still alive and, when it is,
 * puts the store back into admin mode. `checkUserStatus` commits `isAdminMode` and the
 * refreshed auth token itself.
 */
const restoreAdminMode = async function (): Promise<void> {
    try {
        if (!isAdminModePersisted()) return;

        const extUserId = new UserService().getExtUserId();
        const deviceId = StorageHelper.get("browser_id") || "";
        const botId = store.state.extBotId || 0;

        if (Util.isNullOrEmpty(extUserId) || Util.isNullOrEmpty(deviceId)
            || Util.isNullOrEmpty(botId.toString())) {
            return;
        }

        await new UserService().checkUserStatus(botId, deviceId, extUserId);
    } catch (ex) {
        console.log("[admin_mode.ts] An error has occurred \n-> Inside restoreAdminMode -> Error: ", ex);
    }
};

/**
 * Kicks off the admin mode restore exactly once per page load. Safe to call again -
 * later calls reuse the in-flight promise instead of firing a second request.
 */
export const startAdminModeRestore = function (): Promise<void> {
    if (!restorePromise) {
        restorePromise = restoreAdminMode();
    }
    return restorePromise;
};

/**
 * Awaited by every request that carries the admin mode flag, so the flag reflects the
 * restored session rather than the pre-restore default. A no-op when the restore was
 * never started or has already settled.
 */
export const ensureAdminModeRestored = async function (): Promise<void> {
    if (!restorePromise || waitAbandoned) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        // Bounded on purpose - see RESTORE_WAIT_TIMEOUT_MS. On timeout the message goes out
        // with whatever `isAdminMode` currently holds rather than never going out at all.
        const timeout = new Promise<void>((resolve) => {
            timer = setTimeout(resolve, RESTORE_WAIT_TIMEOUT_MS);
        });

        await Promise.race([restorePromise, timeout]);
    } catch {
        // The restore already swallows and logs its own failures; never block a message on it.
    } finally {
        if (timer) clearTimeout(timer);

        // Whichever side won, this is the last wait. Either the restore finished, or it is hung
        // and the flag it would have set is not worth delaying every subsequent message for. A
        // restore that lands late still updates `isAdminMode`, so later turns pick it up anyway.
        waitAbandoned = true;
    }
};

/**
 * Test seam - drops the memoised promise so each test starts from a clean slate.
 */
export const resetAdminModeRestore = function () {
    restorePromise = null;
    waitAbandoned = false;
};

//#endregion
